// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (shared pieces of the Command Ops dashboard and the coming-soon admin screens)
//  · SoonPanel — a mockup panel for something the system can't back yet: a "Coming soon · sample data" badge, dimmed, its controls disabled.
//    Never put real customer data in one, and never invent identities: sample rows use neutral labels.
//  · LandingsTable — the REAL Daily Catch landings from GET /api/admin/drop, read-only (used for the Dashboard's summary card; the full editor is pages/admin/AdminLandings).
//  · StatCard — a headline number with a label and an optional link.
// Neighbours: pages/admin/Dashboard, pages/admin/AdminSoon, pages/admin/AdminLandings, utils/drop.
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Hourglass } from "lucide-react";
import { seafoodItems } from "../../data/seafood";
import { formatLanded, LOW_STOCK, type Landing } from "../../utils/drop";

export const panel = "rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";

export function SoonPanel({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section aria-label={`${title} (coming soon)`} className={`${panel} border border-dashed border-coral-500/40`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-bold">{title}</h2>
          {subtitle && <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{subtitle}</p>}
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-coral-500/10 px-2.5 py-1 text-[11px] font-bold text-coral-600 dark:text-coral-300"><Hourglass className="h-3.5 w-3.5" aria-hidden="true" /> Coming soon · sample data</span>
      </div>
      <div className="mt-3 select-none opacity-60" aria-hidden="true">{children}</div>
    </section>
  );
}

export function StatCard({ label, value, hint, to, testId }: { label: string; value: string | number; hint?: ReactNode; to?: string; testId?: string }) {
  const body = (
    <>
      <p className="text-[11px] font-bold uppercase tracking-wide text-ocean-800/80 dark:text-foam-100/75">{label}</p>
      <p className="mt-1 font-display text-3xl font-bold" data-testid={testId}>{value}</p>
      {hint && <div className="mt-2 text-xs text-ocean-800/85 dark:text-foam-100/80">{hint}</div>}
    </>
  );
  return to ? <Link to={to} className={`${panel} block hover:ring-teal-600`}>{body}</Link> : <div className={panel}>{body}</div>;
}

export function LandingsTable({ landings }: { landings: Landing[] }) {
  if (landings.length === 0) return <p className="rounded-xl bg-ocean-700/5 p-4 text-sm dark:bg-white/5">No landings are set for today.</p>;
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
        <caption className="sr-only">Today&apos;s Daily Catch landings</caption>
        <thead>
          <tr className="bg-ocean-700/5 text-[11px] uppercase text-ocean-800 dark:bg-white/5 dark:text-foam-100">
            <th scope="col" className="rounded-l-lg px-3 py-2">Catch</th>
            <th scope="col" className="px-3 py-2">Boat &amp; spec</th>
            <th scope="col" className="px-3 py-2">Landed</th>
            <th scope="col" className="px-3 py-2">Grade</th>
            <th scope="col" className="rounded-r-lg px-3 py-2 text-right">Packs left</th>
          </tr>
        </thead>
        <tbody>
          {landings.map((landing) => {
            const item = seafoodItems.find((candidate) => candidate.id === landing.itemId);
            return (
              <tr key={landing.id} className="border-b border-ocean-900/5 last:border-0 dark:border-white/10" data-testid="landing-row">
                <td className="px-3 py-3 font-bold">{item?.name ?? landing.itemId}</td>
                <td className="px-3 py-3">{landing.boat ?? "—"}<span className="block text-xs text-ocean-800/75 dark:text-foam-100/70">{landing.spec ?? ""}</span></td>
                <td className="px-3 py-3">{formatLanded(landing.landedAt)}</td>
                <td className="px-3 py-3">{landing.grade ?? "—"}</td>
                <td className={`px-3 py-3 text-right font-bold ${landing.soldOut ? "text-coral-600 dark:text-coral-300" : landing.packsLeft <= LOW_STOCK ? "text-coral-600 dark:text-coral-300" : ""}`}>{landing.soldOut ? "Sold out" : `${landing.packsLeft} / ${landing.packsTotal}`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
